import React, { HTMLAttributes, useEffect, useRef, useState } from 'react';
import { QuoteListTableColumnFilterEnum } from '@/cms/hooks/tableColumnFilter/useQuoteListTableColumnFilter';
import { QuoteBusinessSourceEnum } from '@jove-frontend/enums';
import { useLocalStorage, useLocalTranslation } from '@jove-frontend/uikit';
import { AutoFixHigh, DragIndicator, Refresh } from '@mui/icons-material';
import { Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, IconButton, Popover, PopoverOrigin } from '@mui/material';
import clsx from 'clsx';
import { cloneDeep } from 'lodash';

export interface TableCommonColumnFilterItemProp {
  value: string;
  label: string;
  checked: boolean;
}

export enum TableCommonColumnFilterSourceTypeEnum {
  brokerList = 'brokerList',
}

type TableCommonColumnFilterViewProps = HTMLAttributes<HTMLDivElement> & {
  saveKey: string;
  defaultColumnList: TableCommonColumnFilterItemProp[];
  anchorOrigin?: PopoverOrigin;
  columnChange?: (list: TableCommonColumnFilterItemProp[]) => void;
  btnClassName?: string;
  emitWhenInitial?: boolean;
  sourceType?: QuoteBusinessSourceEnum | TableCommonColumnFilterSourceTypeEnum;
  transformOrigin?: PopoverOrigin;
  // Enable drag-and-drop reordering of the checkbox items
  enableDragSort?: boolean;
  // Column values pinned at the end of the table; hidden from the checkbox list and always emitted as checked
  fixedEndValues?: string[];
};

const getCurrentDefaultColumnList = (list, config?: { sourceType: any }) => {
  return (list || []).filter(
    (item) =>
      !(
        [QuoteBusinessSourceEnum.broker, QuoteBusinessSourceEnum.brokerQuote, QuoteBusinessSourceEnum.brokerDetailInfoView].includes(
          config?.sourceType
        ) && [QuoteListTableColumnFilterEnum.renewalStatus, QuoteListTableColumnFilterEnum.organizationName].includes(item.value as any)
      )
  );
};

const mergeSavedColumnListWithDefault = (list, defaultColumnList: TableCommonColumnFilterItemProp[] = []) => {
  const savedList = (list || []).map((item) => {
    return {
      ...item,
      label: defaultColumnList?.find((aItem) => item.value === aItem.value)?.label || item.label,
    };
  });

  const appendList = defaultColumnList.filter((item) => !savedList.find((aItem) => aItem.value === item.value));

  return [...savedList, ...appendList];
};

// Add judgement later when the column data is updated
const TableCommonColumnFilterView = (props: TableCommonColumnFilterViewProps) => {
  const {
    className,
    anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
    transformOrigin = { vertical: 'top', horizontal: 'left' },
    saveKey = 'table-filter',
    columnChange,
    defaultColumnList,
    btnClassName,
    emitWhenInitial,
    sourceType,
    enableDragSort,
    fixedEndValues,
  } = props;
  const { $t } = useLocalTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverId, setPopoverId] = useState(undefined);
  const [value, setValue] = useLocalStorage(saveKey, undefined);
  const isFixedEndItem = (item) => (fixedEndValues || []).includes(item?.value);
  // Fixed-end columns are not part of the sortable checkbox list
  const [columnFilterList, setColumnFilterList] = useState(
    getCurrentDefaultColumnList(mergeSavedColumnListWithDefault(value || defaultColumnList, defaultColumnList), { sourceType }).filter(
      (item) => !isFixedEndItem(item)
    )
  );
  const originColumnFilterList = useRef(cloneDeep(columnFilterList));
  const dragItemIndexRef = useRef(null);

  /**
   * effects
   */
  useEffect(() => {
    if (emitWhenInitial) {
      refreshColumnAction({ confirmOnce: true });
    }
  }, []);

  /**
   * methods
   */
  const openPopoverAction = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setPopoverId('table-filter-popover');
    refreshColumnAction();
  };

  const closeAction = () => {
    setAnchorEl(null);
    setColumnFilterList(cloneDeep(originColumnFilterList.current));
    setPopoverId(undefined);
  };

  const clickCheckedAction = (item) => {
    const index = columnFilterList.findIndex((aItem) => aItem.value === item.value);
    columnFilterList.splice(index, 1, { ...item, checked: !item.checked });
    setColumnFilterList([...columnFilterList]);
  };

  // Fixed-end columns are always appended at the end as checked
  const getFixedEndItemList = () => {
    return (defaultColumnList || []).filter((item) => isFixedEndItem(item)).map((item) => ({ ...item, checked: true }));
  };

  const confirmAction = (list?: any[]) => {
    const sortableList = list || columnFilterList;
    const emitList = [...sortableList, ...getFixedEndItemList()];
    setValue(emitList);
    columnChange?.(emitList);
    originColumnFilterList.current = cloneDeep(sortableList);
    closeAction();
  };

  const refreshColumnAction = (config?: { confirmOnce?: boolean }) => {
    const newDefaultColumnList = getCurrentDefaultColumnList(defaultColumnList, { sourceType });
    // Keep the current order so user-defined sorting is preserved; only drop removed columns and sync meta from defaults
    const keptList = columnFilterList
      .filter((item) => !!newDefaultColumnList.find((aItem) => aItem.value === item.value) && !isFixedEndItem(item))
      .map((item) => {
        const defaultItem = newDefaultColumnList.find((aItem) => aItem.value === item.value);
        return { ...item, label: defaultItem?.label || item.label, sortable: !!defaultItem?.['sortable'] };
      });
    // Newly added columns are appended at the end of the sortable region
    const addList = newDefaultColumnList.filter((item) => !isFixedEndItem(item) && !keptList.find((aItem) => aItem.value === item.value));
    const newList = [...keptList, ...addList];
    setColumnFilterList(newList);
    if (config?.confirmOnce) {
      confirmAction(newList);
    }
  };

  const dragStartAction = (e: React.DragEvent, index: number) => {
    dragItemIndexRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };

  const dragOverAction = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const dragIndex = dragItemIndexRef.current;
    if (dragIndex === null || dragIndex === index) {
      return;
    }
    const list = [...columnFilterList];
    const [dragItem] = list.splice(dragIndex, 1);
    list.splice(index, 0, dragItem);
    dragItemIndexRef.current = index;
    setColumnFilterList(list);
  };

  const dragEndAction = () => {
    dragItemIndexRef.current = null;
  };

  return (
    <div className={clsx('flex flex-col', className)}>
      <IconButton aria-label="Update display columns" onClick={openPopoverAction}>
        <AutoFixHigh className={clsx('h-5 w-5', btnClassName)} />
      </IconButton>
      <Popover
        id={popoverId}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeAction}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 'none',
              overflow: 'visible',
            },
          },
        }}
      >
        <div className="flex max-h-[80vh] min-h-0 min-w-[200px] flex-col overflow-hidden">
          <div className="flex flex-shrink-0 flex-row items-center justify-between border-b border-gray-200 px-4 py-1">
            <span className="text-brand-secondary text-center text-base font-medium">{$t('common:custom_column')}</span>
            <IconButton aria-label="Refresh" onClick={() => refreshColumnAction()}>
              <Refresh className="text-brand-secondary h-5 w-5" />
            </IconButton>
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <FormControl component="fieldset" variant="standard" className=" px-4 py-2">
              {enableDragSort ? (
                <FormGroup>
                  {columnFilterList.map((item, index) => {
                    return (
                      <Box
                        key={item.value}
                        className="flex flex-row items-center rounded hover:bg-gray-50"
                        onDragOver={(e) => dragOverAction(e, index)}
                      >
                        <Box
                          component="span"
                          draggable
                          onDragStart={(e) => dragStartAction(e, index)}
                          onDragEnd={dragEndAction}
                          className="mr-1 flex cursor-grab items-center text-gray-400 active:cursor-grabbing"
                          aria-label={`Drag to reorder ${item.label}`}
                        >
                          <DragIndicator className="h-5 w-5" />
                        </Box>
                        <FormControlLabel
                          className="m-0 flex-1"
                          control={<Checkbox checked={item.checked} onChange={() => clickCheckedAction(item)} />}
                          label={item.label}
                          sx={{
                            flexWrap: 'nowrap',
                            margin: 0,
                            '& .MuiFormControlLabel-label': {
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            },
                          }}
                        />
                      </Box>
                    );
                  })}
                </FormGroup>
              ) : (
                <FormGroup
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                  }}
                >
                  {columnFilterList.map((item) => {
                    return (
                      <FormControlLabel
                        key={item.value}
                        control={<Checkbox checked={item.checked} onChange={() => clickCheckedAction(item)} name="gilad" />}
                        label={item.label}
                        sx={{
                          flexWrap: 'nowrap',
                          margin: 0,
                          '& .MuiFormControlLabel-label': {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                        }}
                      />
                    );
                  })}
                </FormGroup>
              )}
            </FormControl>
          </div>

          <div className="mt-3 flex flex-shrink-0 flex-row justify-end border-t border-gray-200 bg-white px-4 py-3">
            <Button variant="outlined" size="small" onClick={closeAction}>
              {$t('common:cancel')}
            </Button>
            <Button className="ml-3" size="small" variant="contained" onClick={() => confirmAction()}>
              {$t('common:confirm')}
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default TableCommonColumnFilterView;
